import { useCallback, useEffect, useRef, useState } from 'react'
import { logger } from '@/utils/logging'

const CAST_SDK_URL = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1'

type CastState = 'unavailable' | 'available' | 'connecting' | 'connected'

interface UseGoogleCastOptions {
  receiverApplicationId: string | undefined
  /** Current playback position in seconds — sent to receiver when casting starts. */
  currentTime?: number
  /** HLS source URL to cast. */
  sourceUrl?: string
  /** Video title for cast receiver display. */
  title?: string
  /** Video subtitle for cast receiver display. */
  subtitle?: string
}

interface UseGoogleCastReturn {
  castState: CastState
  deviceName: string | null
  startCast: () => void
  stopCast: () => void
}

/**
 * Hook that loads the Google Cast SDK (CAF) and manages cast sessions.
 *
 * Loads the SDK script once, initializes the CastContext with the receiver app ID,
 * and exposes cast state + controls. When a cast session starts, it loads the
 * current HLS media on the receiver device.
 */
export function useGoogleCast({
  receiverApplicationId,
  currentTime,
  sourceUrl,
  title,
  subtitle,
}: UseGoogleCastOptions): UseGoogleCastReturn {
  const [castState, setCastState] = useState<CastState>('unavailable')
  const [deviceName, setDeviceName] = useState<string | null>(null)
  const contextRef = useRef<cast.framework.CastContext | null>(null)
  const currentTimeRef = useRef(currentTime ?? 0)
  const sourceUrlRef = useRef(sourceUrl)
  const titleRef = useRef(title)
  const subtitleRef = useRef(subtitle)

  // Keep refs in sync so the session handler always has latest values
  currentTimeRef.current = currentTime ?? 0
  sourceUrlRef.current = sourceUrl
  titleRef.current = title
  subtitleRef.current = subtitle

  useEffect(() => {
    if (!receiverApplicationId) return

    const loadMediaOnReceiver = (ctx: cast.framework.CastContext) => {
      const session = ctx.getCurrentSession()
      const url = sourceUrlRef.current
      if (!session || !url) return

      try {
        const mediaInfo = new chrome.cast.media.MediaInfo(url, 'application/x-mpegurl')
        mediaInfo.streamType = chrome.cast.media.StreamType.BUFFERED

        const metadata = new chrome.cast.media.GenericMediaMetadata()
        metadata.metadataType = 0
        if (titleRef.current) metadata.title = titleRef.current
        if (subtitleRef.current) metadata.subtitle = subtitleRef.current
        mediaInfo.metadata = metadata

        const loadRequest = new chrome.cast.media.LoadRequest(mediaInfo)
        loadRequest.autoplay = true
        loadRequest.currentTime = currentTimeRef.current

        session.loadMedia(loadRequest).catch((error) => {
          logger().warn('Failed to load media on Cast receiver', {}, error)
        })
      } catch (error) {
        logger().warn('Failed to create Cast media request', {}, error)
      }
    }

    const initializeCast = () => {
      try {
        const ctx = cast.framework.CastContext.getInstance()
        ctx.setOptions({
          receiverApplicationId,
          autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        })
        contextRef.current = ctx

        const handleCastStateChanged = (event: cast.framework.CastStateEventData) => {
          const state = event.castState
          if (state === cast.framework.CastState.NO_DEVICES_AVAILABLE) {
            setCastState('unavailable')
            setDeviceName(null)
          } else if (state === cast.framework.CastState.NOT_CONNECTED) {
            setCastState('available')
            setDeviceName(null)
          } else if (state === cast.framework.CastState.CONNECTING) {
            setCastState('connecting')
          } else if (state === cast.framework.CastState.CONNECTED) {
            setCastState('connected')
            const session = ctx.getCurrentSession()
            const name = session?.getCastDevice()?.friendlyName ?? null
            setDeviceName(name)
          }
        }

        const handleSessionStateChanged = (event: cast.framework.SessionStateEventData) => {
          const state = event.sessionState
          if (
            state === cast.framework.SessionState.SESSION_STARTED ||
            state === cast.framework.SessionState.SESSION_RESUMED
          ) {
            loadMediaOnReceiver(ctx)
          } else if (state === cast.framework.SessionState.SESSION_ENDED) {
            setDeviceName(null)
          }
        }

        ctx.addEventListener(cast.framework.CastContextEventType.CAST_STATE_CHANGED, handleCastStateChanged)
        ctx.addEventListener(cast.framework.CastContextEventType.SESSION_STATE_CHANGED, handleSessionStateChanged)

        // Check initial state (SDK may have auto-joined an existing session)
        const initialState = ctx.getCastState()
        if (initialState === cast.framework.CastState.CONNECTED) {
          setCastState('connected')
          const session = ctx.getCurrentSession()
          setDeviceName(session?.getCastDevice()?.friendlyName ?? null)
        } else if (initialState !== cast.framework.CastState.NO_DEVICES_AVAILABLE) {
          setCastState('available')
        }
      } catch (error) {
        logger().warn('Failed to initialize Google Cast', {}, error)
      }
    }

    // If the Cast SDK is already loaded, initialize immediately
    if (typeof cast !== 'undefined' && cast.framework) {
      initializeCast()
      return
    }

    // Load the SDK script
    const existingScript = document.querySelector(`script[src="${CAST_SDK_URL}"]`)
    if (!existingScript) {
      window.__onGCastApiAvailable = (isAvailable: boolean) => {
        if (isAvailable) {
          initializeCast()
        }
      }

      const script = document.createElement('script')
      script.src = CAST_SDK_URL
      script.async = true
      document.head.appendChild(script)
    } else {
      // Script already in DOM — wait for the callback or check if already available
      window.__onGCastApiAvailable = (isAvailable: boolean) => {
        if (isAvailable) {
          initializeCast()
        }
      }
    }
  }, [receiverApplicationId])

  const startCast = useCallback(() => {
    const ctx = contextRef.current
    if (!ctx) return
    ctx.requestSession().catch((error) => {
      // User cancelled the dialog — not an error
      logger().info('Cast session request cancelled or failed', { error })
    })
  }, [])

  const stopCast = useCallback(() => {
    const ctx = contextRef.current
    if (!ctx) return
    ctx.endCurrentSession(true)
  }, [])

  return { castState, deviceName, startCast, stopCast }
}
